import { Link } from 'react-router-dom';
import { fmtTime } from '@em/shared';
import { useLive, useSettings } from '../../api';
import { useSession } from '../../auth/AuthContext';
import { minutesToHM } from '../../components/format';
import { Badge, Button, Card, EmptyState, PageHeader, QueryState } from '../../components/ui';

const STATE_LABEL = { in: 'Working', on_break: 'On break', out: 'Out' } as const;

export function LivePage() {
  const session = useSession();
  const settings = useSettings();
  const tz = settings.data?.defaultTimezone ?? 'Australia/Sydney';
  const q = useLive();

  return (
    <>
      <PageHeader
        title="Live board"
        subtitle={q.data ? `Updated ${fmtTime(q.data.now, tz)}. Refreshes every 30 seconds.` : 'Who is on site right now.'}
        actions={
          <Button onClick={() => void q.refetch()} loading={q.isFetching}>
            Refresh
          </Button>
        }
      />
      <QueryState q={q} loadingLabel="Loading live board">
        {(d) => {
          const working = d.onSite.filter((o) => o.state === 'in').length;
          const onBreak = d.onSite.filter((o) => o.state === 'on_break').length;
          return (
            <>
              <div className="grid grid-4" style={{ marginBottom: 16 }}>
                <div className="card stat">
                  <div className="label">Working</div>
                  <div className="value green">{working}</div>
                </div>
                <div className="card stat">
                  <div className="label">On break</div>
                  <div className="value">{onBreak}</div>
                </div>
                <div className="card stat">
                  <div className="label">Rostered, not in</div>
                  <div className={`value ${d.absent.length ? 'red' : ''}`}>{d.absent.length}</div>
                </div>
                <div className="card stat">
                  <div className="label">Late today</div>
                  <div className={`value ${d.lateArrivals.length ? 'amber' : ''}`}>{d.lateArrivals.length}</div>
                </div>
              </div>

              <Card title={`Clocked in (${d.onSite.length})`} tight>
                {d.onSite.length === 0 ? (
                  <EmptyState title="Nobody is clocked in" />
                ) : (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Employee</th>
                          <th>State</th>
                          <th>Since</th>
                          <th className="num">On for</th>
                          <th>Site</th>
                          <th>Source</th>
                          {session.features.jobCosting && <th>Job</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {d.onSite.map((o) => (
                          <tr key={o.employeeId}>
                            <td className="strong">
                              <Link to={`/employees/${o.employeeId}`}>{o.employeeName}</Link>
                            </td>
                            <td>
                              <Badge tone={o.state === 'in' ? 'green' : 'amber'}>{STATE_LABEL[o.state]}</Badge>
                            </td>
                            <td className="nowrap">{fmtTime(o.since, tz)}</td>
                            <td className="num">{minutesToHM(Math.max(0, (Date.parse(d.now) - Date.parse(o.since)) / 60000))}</td>
                            <td>
                              {o.siteName ?? <span className="muted">No site</span>}{' '}
                              {o.outsideGeofence && <Badge tone="amber">Outside geofence</Badge>}
                            </td>
                            <td>{o.source}</td>
                            {session.features.jobCosting && <td>{o.jobCode ?? <span className="muted">–</span>}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              <div className="grid grid-2 section">
                <Card title={`Rostered but not clocked in (${d.absent.length})`} tight>
                  {d.absent.length === 0 ? (
                    <EmptyState title="Everyone rostered is in" />
                  ) : (
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Employee</th>
                          <th>Rostered start</th>
                          <th>Site</th>
                          <th className="num">Late by</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.absent.map((a) => (
                          <tr key={a.employeeId}>
                            <td className="strong">{a.employeeName}</td>
                            <td className="nowrap">{fmtTime(a.rosterStart, tz)}</td>
                            <td>{a.siteName ?? '–'}</td>
                            <td className="num red">{minutesToHM(a.minutesLate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </Card>
                <Card title={`Late arrivals (${d.lateArrivals.length})`} tight>
                  {d.lateArrivals.length === 0 ? (
                    <EmptyState title="No late arrivals" />
                  ) : (
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Employee</th>
                          <th>Rostered</th>
                          <th>Clocked in</th>
                          <th className="num">Minutes late</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.lateArrivals.map((a) => (
                          <tr key={a.employeeId}>
                            <td className="strong">{a.employeeName}</td>
                            <td className="nowrap">{fmtTime(a.rosterStart, tz, false)}</td>
                            <td className="nowrap">{fmtTime(a.clockIn, tz, false)}</td>
                            <td className="num amber">{a.minutesLate}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </Card>
              </div>
            </>
          );
        }}
      </QueryState>
    </>
  );
}
